import { useCallback } from 'react';
import type { FC } from 'react';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { ProviderAccessMap } from '@vgtv/svp-access/lib/access_mappings';
import type { Provider } from '@vgtv/api-client';

import {
  getAssetAccessLevel,
  isEntertainment,
} from '../../shared/helpers/assetHelpers';
import { getSubscriptionUrl } from '../SubscribeButton/getSubscriptionUrl';
import { trackSubscribeClickEvent } from '../../services/tracking/pulseEvents';
import type { ProviderSubscriptionMap } from '../../services/tracking/pulseEvents';
import { config } from '../../config';
import type { ButtonProps } from '../Button/Button';
import { Button } from '../Button/Button';
import { useSessionInvalidation } from '../utils/useSessionInvalidation';
import { getCurrentPageViewData } from '../../services/tracking/helpers/pulseOrigin';

interface SimpleSubscribeButtonProps {
  asset: SvpAssetAdditional;
  label: string;
  price?: number;
  access?: ProviderAccessMap[Provider];
  size: ButtonProps['size'];
}

const SimpleSubscribeButton: FC<SimpleSubscribeButtonProps> = ({
  asset,
  label,
  price,
  access: accessProp,
  size = 'large',
}) => {
  const access = accessProp || getAssetAccessLevel(asset);

  const useABExperiment = config.provider === 'ab' && isEntertainment(asset);
  const subscriptionUrl = getSubscriptionUrl(asset, {
    access,
    code: useABExperiment ? 'AB-3M19-50P-E-VIDEO-149' : undefined,
  });

  const xDomainId = getCurrentPageViewData().xDomainId;

  useSessionInvalidation();

  const handleSubscribeClick = useCallback(
    async (e) => {
      e.preventDefault();

      try {
        await trackSubscribeClickEvent({
          button: {
            elementType: 'SubscribeButton',
            title: label,
          },
          subscription: {
            url: subscriptionUrl.split('?')[0],
            price,
            provider: config.provider,
            name: access as ProviderSubscriptionMap[typeof config.provider],
          },
          xDomainId,
        });
      } catch {
        // noop
      }

      window.location.href = e.target.href;
    },
    [subscriptionUrl, price, label, access, xDomainId]
  );

  return (
    <Button
      size={size}
      component="a"
      variant="contained"
      rounded
      isActiveBrandColor
      href={subscriptionUrl}
      onClick={handleSubscribeClick}
    >
      {label}
    </Button>
  );
};

export { SimpleSubscribeButton };
